/**
 * SameDayAppointmentPrompt (GDN-3909)
 *
 * The offer to move an encounter off a stale appointment link and onto the
 * patient's appointment TODAY, shown when a dictation starts (or a note is
 * generated) on an encounter still bound to another day's slot.
 *
 * Purely presentational and fully controlled: it renders the offer that
 * `findSameDayRelinkOffer` decided was warranted and reports the provider's
 * answer. It performs no write of its own.
 *
 * What accepting means — and does NOT mean:
 *   - Accepting RE-LINKS the encounter to the same-day appointment. Which
 *     appointment an encounter belongs to is a link, not a time.
 *   - It is NOT an official-time patch. It must never send scheduled_for and
 *     must never move a calendar slot (see the WRITE SEMANTICS block in
 *     officialTimeModel.ts). Once re-linked, the official time follows from the
 *     new link through the normal selector path.
 *   - Declining leaves the link exactly as it was; the selector then shows the
 *     linked slot muted and dated ("Scheduled · Sep 17 — another day") so the
 *     mismatch stays visible rather than silently preselected.
 */

import React from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';

import {
  formatClock,
  formatSameDayRelinkHeadline,
  formatSameDayRelinkQuestion,
  type SameDayRelinkOffer,
  toLocalTimeString,
} from './officialTimeModel';

export interface SameDayAppointmentPromptProps {
  open: boolean;
  /**
   * The warranted offer. Null renders nothing — the caller may keep the
   * component mounted while no offer stands.
   */
  offer: SameDayRelinkOffer | null;
  /** Re-link the encounter to `offer.candidate`. */
  onAccept: () => void;
  /** Keep the existing link; the dialog closes. */
  onDecline: () => void;
  /** The re-link write is in flight. */
  loading?: boolean;
}

export const SameDayAppointmentPrompt: React.FC<SameDayAppointmentPromptProps> = ({
  open,
  offer,
  onAccept,
  onDecline,
  loading = false,
}) => {
  if (!offer) return null;

  const offeredClock = formatClock(toLocalTimeString(offer.offeredStart));

  return (
    <ConfirmDialog
      open={open}
      variant="warning"
      // The model owns the copy so the prompt and its tests cannot drift. The
      // two halves of the sentence split across title and body — rendering the
      // whole string in the body would repeat the title verbatim.
      title={formatSameDayRelinkHeadline(offer)}
      message={formatSameDayRelinkQuestion(offer)}
      onClose={onDecline}
      onConfirm={onAccept}
      confirmText={`Use today's ${offeredClock} appointment`}
      cancelText="Keep current link"
      loading={loading}
      maxWidth="sm"
      fullWidth
    />
  );
};
